/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-plusplus */
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, ListGroup, Badge } from 'react-bootstrap';
import { v4 } from 'uuid';
import moment from 'moment';

import Stats1 from '../../components/Dashboards/Stats1';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import PageHeader from '../../components/PageHeader';
import Loading from '../../components/Loading';
import ErrorProcessor from '../../components/ErrorProcessor';
import Activity from '../../components/Activity';
import RankingCard from '../../components/RankingCard';

export default function Home() {
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [leads, setLeads] = useState({});
  const [slaStats, setSlaStats] = useState({
    paused: 0,
    attended: 0,
    overdueTodayAtt: 0, // SLA de atendimento atrasado hoje
    overdueMonthAtt: 0, // SLA de atendimento atrasado no mês
    overdueTodaySol: 0, // SLA de solução atrasado hoje
    overdueMonthSol: 0, // SLA de solução atrasado no mês
    ticketsInProgress: 0, // Tickets em atendimento
  });
  const [overdueTickets, setOverdueTickets] = useState([]);
  const [overdueGeneralTickets, setOverdueGeneralTickets] = useState([]);
  const [topAttendantsMonth, setTopAttendantsMonth] = useState([]);
  const [topAttendantsGeneral, setTopAttendantsGeneral] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topCustomersMonth, setTopCustomersMonth] = useState([]);
  const [ticketsByDepartmentMonth, setTicketsByDepartmentMonth] = useState([]);
  const [ticketsByDepartmentWeek, setTicketsByDepartmentWeek] = useState([]);
  const [ticketsByOrigemWeek, setTicketsByOrigemWeek] = useState([]);
  const [ticketsByOrigemMonth, setTicketsByOrigemMonth] = useState([]);

  const getData = useCallback(async (loading = true) => {
    if (loading) {
      setIsLoading(true);
    }
    try {
      const [
        statsResponse,
        last30TicketsResponse,
        userDepartmentsResponse,
        topAttendantsMonthResponse,
        topAttendantsWeekResponse,
        topCustomersWeekResponse,
        topCustomersMonthResponse,
        ticketsByDepartmentMonthResponse,
        ticketsByDepartmentWeekResponse,
        ticketsByOrigemWeekResponse,
        ticketsByOrigemMonthResponse,
        slaOverdueTodayResponse,
        slaOverdueMonthResponse,
        slaOverdueGeneralResponse,
      ] = await Promise.all([
        axios.get('/stats/'),
        axios.get('/tickets/last30-optimized'),
        axios.get('/userdpto'),
        axios.get('/stats/top-attendants-month'),
        axios.get('/stats/top-attendants-week'),
        axios.get('/stats/top-customers-week'),
        axios.get('/stats/top-customers-month'),
        axios.get('/stats/tickets-by-department-month'),
        axios.get('/stats/tickets-by-department-week'),
        axios.get('/stats/tickets-by-origem-week'),
        axios.get('/stats/tickets-by-origem-month'),
        axios.get('/stats/sla-overdue-today'),
        axios.get('/stats/sla-overdue-month'),
        axios.get('/stats/sla-overdue-general'),
      ]);

      const statsData = statsResponse.data;
      const last30Tickets = last30TicketsResponse.data;
      const userDepartments = userDepartmentsResponse.data;

      // Mapear os usuários e departamentos
      const userMap = userDepartments.reduce((acc, userDept) => {
        acc[userDept.UserProfile.User.id] =
          `${userDept.UserProfile.name} ${userDept.UserProfile.lastname}`;
        return acc;
      }, {});

      const departmentMap = userDepartments.reduce((acc, userDept) => {
        acc[userDept.departmentId] = userDept.Department.name;
        return acc;
      }, {});

      let pausedCount = 0;
      let attendedCount = 0;
      let overdueTodayAtt = 0;
      let overdueMonthAtt = 0;
      let overdueTodaySol = 0;
      let overdueMonthSol = 0;
      let ticketsInProgress = 0; // Tickets em atendimento
      const overdueTicketsList = []; // Lista de tickets com SLA vencido

      // Calcular a quantidade de tickets com SLA pausado, atendido e em atendimento
      last30Tickets.forEach((ticket) => {
        // Os dados do ServiceDesk e Priority já vêm incluídos no ticket otimizado
        const priority = ticket.ticketPriority;
        const atendimentoVencimento = priority
          ? priority.atendimentoVencimento
          : null;
        const solucaoVencimento = priority ? priority.solucaoVencimento : null;

        const currentTime = moment();
        const createdTime = moment(ticket.createdAt);

        if (ticket.status === 'pausado') {
          pausedCount++;
        }

        // Tickets em atendimento
        if (ticket.status === 'em_atendimento') {
          ticketsInProgress++;
        }

        // SLA de Atendimento
        if (!ticket.startTime && atendimentoVencimento) {
          const atendimentoDeadline = createdTime
            .clone()
            .add(moment.duration(atendimentoVencimento));

          if (currentTime.isAfter(atendimentoDeadline)) {
            if (currentTime.isSame(createdTime, 'day')) {
              overdueTodayAtt++; // SLA de atendimento atrasado hoje
            }
            overdueMonthAtt++; // SLA de atendimento atrasado no mês

            // Adicionar à lista de tickets com SLA vencido
            overdueTicketsList.push({
              id: ticket.id,
              title: ticket.title,
              type: 'Atendimento',
              status: 'SLA Atendimento Vencido',
              assignedTo: userMap[ticket.assignedTo] || 'Não atribuído',
              departmentName:
                departmentMap[ticket.department] || 'Sem departamento',
              createdAt: ticket.createdAt,
              overdueTime: atendimentoDeadline.fromNow(),
            });
          }
        }

        // SLA de Solução
        if (ticket.startTime && solucaoVencimento) {
          const startTime = moment(ticket.startTime);
          const solutionDeadline = startTime
            .clone()
            .add(moment.duration(solucaoVencimento));

          if (currentTime.isAfter(solutionDeadline)) {
            if (currentTime.isSame(startTime, 'day')) {
              overdueTodaySol++; // SLA de solução atrasado hoje
            }
            overdueMonthSol++; // SLA de solução atrasado no mês

            // Adicionar à lista de tickets com SLA vencido (apenas se não estiver fechado)
            if (ticket.status !== 'fechado') {
              overdueTicketsList.push({
                id: ticket.id,
                title: ticket.title,
                type: 'Solução',
                status: 'SLA Solução Vencido',
                assignedTo: userMap[ticket.assignedTo] || 'Não atribuído',
                departmentName:
                  departmentMap[ticket.department] || 'Sem departamento',
                createdAt: ticket.createdAt,
                overdueTime: solutionDeadline.fromNow(),
              });
            }
          }

          // Se a solução foi completada a tempo
          if (ticket.closingTime && currentTime.isBefore(solutionDeadline)) {
            attendedCount++; // SLA de solução atendido
          }
        }
      });

      setSlaStats({
        paused: pausedCount,
        attended: attendedCount,
        overdueTodayAtt,
        overdueMonthAtt,
        overdueTodaySol,
        overdueMonthSol,
        ticketsInProgress,
      });

      // Ordenar tickets vencidos por data de criação (mais antigos primeiro)
      const overdueApi = slaOverdueTodayResponse.data || { tickets: [] };
      const overdueMonthApi = slaOverdueMonthResponse.data || {
        count: 0,
        tickets: [],
      };
      const overdueGeneralApi = slaOverdueGeneralResponse.data || {
        count: 0,
        tickets: [],
      };
      setOverdueTickets(
        (overdueApi.tickets || []).map((t) => ({
          id: t.id,
          title: t.title,
          type: t.type,
          status: t.status,
          assignedTo: t.assignedUserName,
          customerName: t.customerName,
          overdueTime: moment(t.overdueDeadline).fromNow(),
        }))
      );

      const todayTickets = overdueApi.tickets || [];
      const todayAttCount = todayTickets.filter(
        (t) => t.type === 'Atendimento'
      ).length;
      const todaySolCount = todayTickets.filter(
        (t) => t.type === 'Solução'
      ).length;

      const generalTickets = overdueGeneralApi.tickets || [];
      const generalAttCount = generalTickets.filter(
        (t) => t.type === 'Atendimento'
      ).length;
      const generalSolCount = generalTickets.filter(
        (t) => t.type === 'Solução'
      ).length;

      setOverdueGeneralTickets(
        generalTickets.slice(0, 10).map((t) => ({
          id: t.id,
          title: t.title,
          type: t.type,
          assignedUserName: t.assignedUserName,
          customerName: t.customerName,
          overdueTime: moment(t.overdueDeadline).fromNow(),
        }))
      );

      const monthTickets = overdueMonthApi.tickets || [];
      const monthAttCount = monthTickets.filter(
        (t) => t.type === 'Atendimento'
      ).length;
      const monthSolCount = monthTickets.filter(
        (t) => t.type === 'Solução'
      ).length;

      // Formatar os tickets para incluir nomes de usuários e departamentos
      const formattedTickets = last30Tickets.map((ticket) => ({
        ...ticket,
        assignedTo: userMap[ticket.assignedTo] || 'Usuário Desconhecido',
        departmentName:
          departmentMap[ticket.department] || 'Departamento Desconhecido',
      }));

      // Formatar leadsByDepartment com nomes de departamentos
      const leadsByDepartment = statsData.ticketsByDepartment.map((dept) => ({
        ...dept,
        departmentName:
          departmentMap[dept.department] || 'Departamento Desconhecido',
      }));

      // Formatar leadsByUser com nomes de usuários
      const leadsByUser = statsData.ticketsByUser.map((user) => ({
        ...user,
        userName: userMap[user.assignedTo] || 'Usuário Desconhecido',
      }));

      // Formatar leadsByDepartmentMonth com nomes de departamentos
      const leadsByDepartmentMonth = (
        ticketsByDepartmentMonthResponse.data || []
      ).map((dept) => ({
        ...dept,
        departmentName:
          departmentMap[dept.department] || 'Departamento Desconhecido',
      }));

      // Formatar leadsByDepartmentWeek com nomes de departamentos
      const leadsByDepartmentWeek = (
        ticketsByDepartmentWeekResponse.data || []
      ).map((dept) => ({
        ...dept,
        departmentName:
          departmentMap[dept.department] || 'Departamento Desconhecido',
      }));

      // Processar dados dos rankings - mapear para o formato esperado pelo RankingCard
      const formattedTopAttendantsMonth = (
        topAttendantsMonthResponse.data || []
      ).map((attendant) => ({
        name: attendant.attendantName,
        count: attendant.totalTickets,
        department: attendant.attendantEmail,
      }));

      const formattedTopAttendantsWeek = (
        topAttendantsWeekResponse.data || []
      ).map((attendant) => ({
        name: attendant.attendantName,
        count: attendant.totalTickets,
        department: attendant.attendantEmail,
      }));

      const formattedTopCustomersWeek = (
        topCustomersWeekResponse.data || []
      ).map((customer) => ({
        customerName: customer.customerName,
        count: customer.totalTickets,
      }));

      const formattedTopCustomersMonth = (
        topCustomersMonthResponse.data || []
      ).map((customer) => ({
        customerName: customer.customerName,
        count: customer.totalTickets,
      }));

      setTopAttendantsMonth(formattedTopAttendantsMonth);
      setTopAttendantsGeneral(formattedTopAttendantsWeek);
      setTopCustomers(formattedTopCustomersWeek);
      setTopCustomersMonth(formattedTopCustomersMonth);
      setTicketsByDepartmentMonth(leadsByDepartmentMonth);
      setTicketsByDepartmentWeek(leadsByDepartmentWeek);
      setTicketsByOrigemWeek(ticketsByOrigemWeekResponse.data || []);
      setTicketsByOrigemMonth(ticketsByOrigemMonthResponse.data || []);

      setLeads({
        todayLeads: statsData.ticketsOpenedToday,
        weekLeads: statsData.ticketsOpenedThisWeek,
        monthLeads: statsData.ticketsOpenedThisMonth,
        leadsClosedToday: statsData.ticketsClosedToday,
        leadsClosedThisWeek: statsData.ticketsClosedThisWeek,
        leadsClosedThisMonth: statsData.ticketsClosedThisMonth,
        leadsByDepartment, // Usar os nomes dos departamentos mapeados
        leadsByUser, // Usar os nomes dos usuários mapeados
        last30Tickets: formattedTickets, // Armazena os últimos 30 tickets formatados
        ticketsInProgress: statsData.ticketsInProgress,
        ticketsPaused: statsData.ticketsPaused,
        totalTicketsClosedThisMonth: statsData.totalTicketsClosedThisMonth, // Tickets fechados no mês
        ticketsInProgressToday: statsData.ticketsInProgressToday,
        ticketsInProgressMonth: statsData.ticketsInProgressMonth,
        ticketsPausedToday: statsData.ticketsPausedToday,
        ticketsPausedMonth: statsData.ticketsPausedMonth,
        slaOverdueMonth: overdueMonthApi.count,
        slaOverdueMonthAtt: monthAttCount || 0,
        slaOverdueMonthSol: monthSolCount || 0,
        slaOverdueGeneral: overdueGeneralApi.count,
        slaOverdueGeneralAtt:
          (overdueGeneralApi.countAtt ?? generalAttCount) || 0,
        slaOverdueGeneralSol:
          (overdueGeneralApi.countSol ?? generalSolCount) || 0,
        slaOverdueTodayAtt: todayAttCount || 0,
        slaOverdueTodaySol: todaySolCount || 0,
      });
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
      setDataLoaded(true);
    }
  }, []);

  useEffect(() => {
    getData();
    const intervalId = setInterval(() => {
      getData(false);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [getData]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <ErrorProcessor error={apiErrors} />
      <Loading isLoading={isLoading} />
      <Breadcrumbs title="Dashboards" />
      <PageHeader title="Dashboards" />
      <section className="mb-3 mb-lg-5">
        {dataLoaded && !apiErrors && (
          <>
            {/* Seção de Rankings - Movida para o topo */}
            <Row className="mt-4 mb-4">
              <Col xs={12}>
                <h4 className="mb-3">
                  <i className="fas fa-trophy text-warning me-2" />
                  Rankings
                </h4>
              </Col>
            </Row>

            <Row className="g-4 mb-4">
              <Col xs={12} lg={6}>
                <RankingCard
                  title="📞 TOP 10 Clientes - Mês Atual"
                  data={topCustomersMonth}
                  type="customer"
                  headerColor="success"
                />
              </Col>
              <Col xs={12} lg={6}>
                <RankingCard
                  title="🏆 TOP 10 Atendentes - Mês Atual"
                  data={topAttendantsMonth}
                  type="attendant"
                  headerColor="success"
                />
              </Col>
            </Row>
            <Row className="g-4 mb-4">
              <Col xs={12} lg={6}>
                <RankingCard
                  title="📞 TOP 10 Clientes - Semana Atual"
                  data={topCustomers}
                  type="customer"
                  headerColor="success"
                />
              </Col>
              <Col xs={12} lg={6}>
                <RankingCard
                  title="🥇 TOP 10 Atendentes - Semana Atual"
                  data={topAttendantsGeneral}
                  type="attendant"
                  headerColor="success"
                />
              </Col>
            </Row>

            {/* Seção de Tickets por Departamento */}
            <Row className="mb-4">
              {/* Tickets por Departamento - Semana Atual */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <h4 className="card-heading">
                      Tickets por Departamento - Semana Atual
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      {ticketsByDepartmentWeek.map((department) => (
                        <Col xs={12} sm={6} key={v4()}>
                          <Card
                            className="text-center shadow-sm"
                            style={{
                              backgroundColor: '#d4edda',
                              borderColor: '#c3e6cb',
                            }}
                          >
                            <Card.Body>
                              <h5
                                className="card-title"
                                style={{ color: '#155724' }}
                              >
                                {department.departmentName}
                              </h5>
                              <p
                                className="card-text"
                                style={{
                                  color: '#155724',
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                }}
                              >
                                {department.count.toLocaleString('pt-BR')}{' '}
                                Tickets
                              </p>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Tickets por Departamento - Mês Atual */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <h4 className="card-heading">
                      Tickets por Departamento - Mês Atual
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      {ticketsByDepartmentMonth.map((department) => (
                        <Col xs={12} sm={6} key={v4()}>
                          <Card
                            className="text-center shadow-sm"
                            style={{
                              backgroundColor: '#cce5ff',
                              borderColor: '#99ccff',
                            }}
                          >
                            <Card.Body>
                              <h5
                                className="card-title"
                                style={{ color: '#004085' }}
                              >
                                {department.departmentName}
                              </h5>
                              <p
                                className="card-text"
                                style={{
                                  color: '#004085',
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                }}
                              >
                                {department.count.toLocaleString('pt-BR')}{' '}
                                Tickets
                              </p>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Seção de Tickets por Origem */}
            <Row className="mb-4">
              {/* Tickets por Origem - Semana Atual */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <h4 className="card-heading">
                      Tickets por Origem - Semana Atual
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      {ticketsByOrigemWeek.length === 0 ? (
                        <Col xs={12}>
                          <p className="text-muted text-center mb-0">
                            Nenhum ticket com origem registrada esta semana.
                          </p>
                        </Col>
                      ) : (
                        ticketsByOrigemWeek.map((item) => (
                          <Col xs={12} sm={6} key={v4()}>
                            <Card
                              className="text-center shadow-sm"
                              style={{
                                backgroundColor: '#e8d5fb',
                                borderColor: '#d0aaf7',
                              }}
                            >
                              <Card.Body>
                                <h5
                                  className="card-title"
                                  style={{ color: '#4a1a7e' }}
                                >
                                  {item.origem}
                                </h5>
                                <p
                                  className="card-text"
                                  style={{
                                    color: '#4a1a7e',
                                    fontWeight: 'bold',
                                    fontSize: '1.2rem',
                                  }}
                                >
                                  {Number(item.count).toLocaleString('pt-BR')}{' '}
                                  Tickets
                                </p>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Tickets por Origem - Mês Atual */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <h4 className="card-heading">
                      Tickets por Origem - Mês Atual
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      {ticketsByOrigemMonth.length === 0 ? (
                        <Col xs={12}>
                          <p className="text-muted text-center mb-0">
                            Nenhum ticket com origem registrada este mês.
                          </p>
                        </Col>
                      ) : (
                        ticketsByOrigemMonth.map((item) => (
                          <Col xs={12} sm={6} key={v4()}>
                            <Card
                              className="text-center shadow-sm"
                              style={{
                                backgroundColor: '#fde8fb',
                                borderColor: '#f7aaf0',
                              }}
                            >
                              <Card.Body>
                                <h5
                                  className="card-title"
                                  style={{ color: '#7e1a78' }}
                                >
                                  {item.origem}
                                </h5>
                                <p
                                  className="card-text"
                                  style={{
                                    color: '#7e1a78',
                                    fontWeight: 'bold',
                                    fontSize: '1.2rem',
                                  }}
                                >
                                  {Number(item.count).toLocaleString('pt-BR')}{' '}
                                  Tickets
                                </p>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-2">
              {/* Exibe tickets abertos hoje */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="green"
                  icon="sales-performance-up-1"
                  number={leads.todayLeads.toLocaleString('pt-BR') || '0'}
                  name="Tickets Abertos Hoje"
                  footer={`Fechados: ${leads.leadsClosedToday.toLocaleString('pt-BR') || '0'}`}
                />
              </Col>
              {/* Tickets Abertos na Semana */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="green"
                  icon="sales-performance-up-1"
                  number={leads.weekLeads.toLocaleString('pt-BR') || '0'}
                  name="Tickets Abertos na Semana"
                  footer={`Fechados: ${leads.leadsClosedThisWeek.toLocaleString('pt-BR') || '0'}`}
                />
              </Col>
              {/* Tickets Abertos no Mês */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="green"
                  icon="sales-performance-up-1"
                  number={leads.monthLeads.toLocaleString('pt-BR') || '0'}
                  name="Tickets Abertos no Mês"
                  footer={`Fechados: ${leads.leadsClosedThisMonth.toLocaleString('pt-BR') || '0'}`}
                />
              </Col>

              {/* Em Atendimento - Geral */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="blue"
                  icon="view-1"
                  number={
                    leads.ticketsInProgress?.toLocaleString('pt-BR') || '0'
                  }
                  name="Em Atendimento (Geral)"
                />
              </Col>
              {/* Em Atendimento - Hoje */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="blue"
                  icon="view-1"
                  number={
                    leads.ticketsInProgressToday?.toLocaleString('pt-BR') || '0'
                  }
                  name="Em Atendimento (Hoje)"
                />
              </Col>
              {/* Em Atendimento - Mês */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="blue"
                  icon="view-1"
                  number={
                    leads.ticketsInProgressMonth?.toLocaleString('pt-BR') || '0'
                  }
                  name="Em Atendimento (Mês)"
                />
              </Col>

              {/* SLA Vencidos */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="red"
                  icon="close-1"
                  number={overdueTickets.length.toLocaleString('pt-BR') || '0'}
                  name="SLA Vencidos Hoje"
                  footer={`Atendimento: ${leads.slaOverdueTodayAtt || 0} • Solução: ${leads.slaOverdueTodaySol || 0}`}
                />
              </Col>
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="red"
                  icon="close-1"
                  number={(leads.slaOverdueMonth || 0).toLocaleString('pt-BR')}
                  name="SLA Vencidos no Mês"
                  footer={`Atendimento: ${leads.slaOverdueMonthAtt || 0} • Solução: ${leads.slaOverdueMonthSol || 0}`}
                />
              </Col>
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="red"
                  icon="close-1"
                  number={(leads.slaOverdueGeneral || 0).toLocaleString(
                    'pt-BR'
                  )}
                  name="SLA Vencidos (Geral)"
                  footer={`Atendimento: ${leads.slaOverdueGeneralAtt || 0} • Solução: ${leads.slaOverdueGeneralSol || 0}`}
                />
              </Col>

              {/* SLA de Atendimento Atrasado no Mês */}
              {/* <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="red"
                  icon="close-1"
                  number={
                    (
                      slaStats.overdueMonthAtt + slaStats.overdueMonthSol
                    ).toLocaleString('pt-BR') || '0'
                  }
                  name="SLA Atrasado Mês"
                />
              </Col> */}

              {/* SLA de Atendimento Atendido
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="green"
                  icon="checkmark-1"
                  number={slaStats.attended.toLocaleString('pt-BR') || '0'}
                  name="SLA Atendido"
                />
              </Col> */}

              {/* Pausado - Geral */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="orange"
                  icon="time-1"
                  number={leads.ticketsPaused?.toLocaleString('pt-BR') || '0'}
                  name="Pausado (Geral)"
                />
              </Col>
              {/* Pausado - Hoje */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="orange"
                  icon="time-1"
                  number={
                    leads.ticketsPausedToday?.toLocaleString('pt-BR') || '0'
                  }
                  name="Pausado (Hoje)"
                />
              </Col>
              {/* Pausado - Mês */}
              <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="orange"
                  icon="time-1"
                  number={
                    leads.ticketsPausedMonth?.toLocaleString('pt-BR') || '0'
                  }
                  name="Pausado (Mês)"
                />
              </Col>
              {/* Tickets Fechados no Mês */}
              {/* <Col sm={4} lg={4} key={v4()} className="mb-4">
                <Stats1
                  color="red"
                  icon="close-1"
                  number={
                    leads.totalTicketsClosedThisMonth.toLocaleString('pt-BR') ||
                    '0'
                  }
                  name="Tickets Fechados no Mês"
                />
              </Col> */}
            </Row>

            {overdueGeneralTickets.length > 0 && (
              <Row className="mb-4">
                <Col lg={12}>
                  <Card className="h-100 border-danger">
                    <Card.Header className="bg-danger text-white">
                      <h4 className="card-heading mb-0">
                        <i className="fas fa-exclamation-triangle me-2" />
                        Tickets Atrasados (Geral)
                      </h4>
                      <small className="text-white-50">
                        {overdueGeneralTickets.length} itens visíveis
                      </small>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        {overdueGeneralTickets.map((t) => (
                          <ListGroup.Item
                            key={t.id}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <div className="d-flex flex-column">
                              <a
                                href={`/tickets/${t.id}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary fw-bold"
                                title={`Abrir ticket #${t.id}`}
                              >
                                #{t.id} — {t.title}
                              </a>
                              <small className="text-muted">
                                {t.customerName} • {t.assignedUserName}
                              </small>
                            </div>
                            <div className="text-end">
                              <Badge
                                bg={
                                  t.type === 'Atendimento'
                                    ? 'warning'
                                    : 'danger'
                                }
                              >
                                {t.type}
                              </Badge>
                              <div className="small text-danger mt-1">
                                {t.overdueTime}
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Seção de Tickets com SLA Vencido */}
            {overdueTickets.length > 0 && (
              <Row className="mb-4">
                <Col lg={12} className="mb-4">
                  <Card className="h-100 border-danger">
                    <Card.Header className="bg-danger text-white">
                      <h4 className="card-heading mb-0">
                        <i className="fas fa-exclamation-triangle me-2" />
                        Tickets com SLA Vencido
                      </h4>
                      <small className="text-white-50">
                        {overdueTickets.length} ticket
                        {overdueTickets.length !== 1 ? 's' : ''} com SLA vencido
                      </small>
                    </Card.Header>
                    <Card.Body>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Ticket</th>
                              <th>Título</th>
                              <th>Cliente</th>
                              <th>Tipo SLA</th>
                              <th>Status</th>
                              <th>Responsável</th>
                              <th>Vencido há</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {overdueTickets.slice(0, 10).map((ticket) => (
                              <tr key={ticket.id} className="align-middle">
                                <td>
                                  <span className="badge bg-primary">
                                    #{ticket.id}
                                  </span>
                                </td>
                                <td>
                                  <div
                                    className="text-truncate"
                                    style={{ maxWidth: '200px' }}
                                    title={ticket.title}
                                  >
                                    {ticket.title}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    className="text-truncate"
                                    style={{ maxWidth: '180px' }}
                                    title={ticket.customerName}
                                  >
                                    {ticket.customerName}
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className={`badge ${
                                      ticket.type === 'Atendimento'
                                        ? 'bg-warning'
                                        : 'bg-danger'
                                    }`}
                                  >
                                    {ticket.type}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-danger">
                                    <i className="fas fa-clock me-1" />
                                    {ticket.status}
                                  </span>
                                </td>
                                <td>
                                  <div
                                    className="text-truncate"
                                    style={{ maxWidth: '120px' }}
                                    title={ticket.assignedTo}
                                  >
                                    {ticket.assignedTo}
                                  </div>
                                </td>
                                <td>
                                  <span className="text-danger fw-bold">
                                    {ticket.overdueTime}
                                  </span>
                                </td>
                                <td>
                                  <a
                                    href={`/tickets/${ticket.id}/edit`}
                                    className="btn btn-sm btn-outline-primary"
                                    title="Visualizar ticket"
                                  >
                                    <i className="fas fa-eye" />
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {overdueTickets.length > 10 && (
                        <div className="text-center mt-3">
                          <small className="text-muted">
                            Mostrando os primeiros 10 tickets. Total:{' '}
                            {overdueTickets.length}
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            <Row>
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <h4 className="card-heading">
                      Últimos 30 Tickets Atualizados
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <Activity
                      title=""
                      data={leads.last30Tickets.map((ticket) => ({
                        name: `${ticket.title} - ${ticket.departmentName}`,
                        time: moment.utc(ticket.createdAt).format('DD/MM/YYYY'),
                        text: `Ticket #${ticket.id} - Responsável: ${ticket.assignedTo}`,
                        link: `/tickets/${ticket.id}/edit`,
                      }))}
                      appendHeader={
                        <>
                          <Col
                            sm={12}
                            className="border-bottom d-flex justify-content-center align-items-center"
                          >
                            <div className="h6 text-gray-500 me-3">Total </div>
                            <div className="h2 text-primary">
                              {leads.last30Tickets.length || 0}
                            </div>
                          </Col>
                          <Col sm={12} className="mt-3">
                            <h6 className="subtitle text-gray-500">
                              Tickets Atualizados Recentemente
                            </h6>
                          </Col>
                        </>
                      }
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header className="bg-success text-white">
                    <h4 className="card-heading mb-0">
                      <i className="fas fa-check-circle me-2" />
                      Tickets Fechados no Mês
                    </h4>
                    <small className="text-white-50">
                      {moment().format('MMMM YYYY')}
                    </small>
                  </Card.Header>
                  <Card.Body>
                    {leads.leadsByUser.length > 0 ? (
                      <Row className="g-3">
                        {leads.leadsByUser.map((user) => (
                          <Col xs={12} sm={6} key={v4()}>
                            <Card className="border-0 bg-light h-100">
                              <Card.Body className="text-center p-3">
                                <div className="mb-2">
                                  <i
                                    className="fas fa-user-circle text-success"
                                    style={{ fontSize: '2rem' }}
                                  />
                                </div>
                                <h6
                                  className="card-title text-truncate mb-1"
                                  title={user.userName}
                                >
                                  {user.userName}
                                </h6>
                                <div className="d-flex justify-content-center align-items-center">
                                  <span className="badge bg-success fs-6 px-3 py-2">
                                    {user.count.toLocaleString('pt-BR')}
                                  </span>
                                </div>
                                <small className="text-muted mt-1 d-block">
                                  {user.count === 1
                                    ? 'ticket fechado'
                                    : 'tickets fechados'}
                                </small>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <div className="text-center py-4">
                        <i
                          className="fas fa-inbox text-muted"
                          style={{ fontSize: '3rem' }}
                        />
                        <h5 className="text-muted mt-3">
                          Nenhum ticket fechado
                        </h5>
                        <p className="text-muted">
                          Nenhum ticket foi fechado neste mês ainda.
                        </p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </section>
    </Container>
  );
}
